/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-02-13 14:13:55
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import { getJSON } from 'redux-api-middleware';
import { I18n } from '@shopify/react-i18n';
import { processRes } from 'src/helpers/actions-helper';

import apiService from 'src/services/api-service';
import {
  VERIFY_DISCOUNT_CODE_REQUEST,
  VERIFY_DISCOUNT_CODE_SUCCESS,
  VERIFY_DISCOUNT_CODE_FAILURE,
  SUBSCRIBE_PLAN_REQUEST,
  SUBSCRIBE_PLAN_SUCCESS,
  SUBSCRIBE_PLAN_FAILURE,
  ACTIVATE_CHARGE_REQUEST,
  ACTIVATE_CHARGE_SUCCESS,
  ACTIVATE_CHARGE_FAILURE,
  CLEAR_DISCOUNT_CODE,
  SUBSCRIBE_ADDON_REQUEST,
  SUBSCRIBE_ADDON_SUCCESS,
  SUBSCRIBE_ADDON_FAILURE,
  UNSUBSCRIBE_ADDON_REQUEST,
  UNSUBSCRIBE_ADDON_SUCCESS,
  UNSUBSCRIBE_ADDON_FAILURE,
  GET_ADDONS_REQUEST,
  GET_ADDONS_SUCCESS,
  GET_ADDONS_FAILURE,
  GET_ADDON_STATUS_REQUEST,
  GET_ADDON_STATUS_SUCCESS,
  GET_ADDON_STATUS_FAILURE,
  GET_PACKAGE_PLANS_FAILURE,
  GET_PACKAGE_PLANS_SUCCESS,
  GET_PACKAGE_PLANS_REQUEST,
  GET_INVOICE_PLANS_REQUEST,
  GET_INVOICE_PLANS_SUCCESS,
  GET_INVOICE_PLANS_FAILURE
} from 'stores/flow-type/subscription-v3.type';

/**
 * call to verify discount code
 * @return {object} The RSAA object for API middleware
 */
export const verifyDiscountCode = (code, subscribeOn, addOns, newPlanName) => {
  return apiService.sendRequest(
    'admin-v3.discount-code.verify',
    { body: { code, subscribeOn, addOns, newPlanName } },
    [
      VERIFY_DISCOUNT_CODE_REQUEST,
      {
        type: VERIFY_DISCOUNT_CODE_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data, subscribeOn };
        }
      },
      {
        type: VERIFY_DISCOUNT_CODE_FAILURE,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      }
    ]
  );
};

/**
 * call to subscribe a plan
 * @return {object} The RSAA object for API middleware
 */
export const subscribe = (planId: string, code: string, host, addOns = [], i18n: I18n) => {
  const params = { planId };
  // Prepare Body
  let dataBody = { host, addOns };
  if (code) {
    dataBody = _.assign(dataBody, {
      discountCode: code
    });
  }

  return apiService.sendRequest('admin-v3.subscription-v3.subscribe', { params, body: dataBody }, [
    SUBSCRIBE_PLAN_REQUEST,
    {
      type: SUBSCRIBE_PLAN_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: SUBSCRIBE_PLAN_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.SubscriptionV3.Actions.subscribe'),
          i18n
        )
    }
  ]);
};

/**
 * call to subscribe a plan
 * @return {object} The RSAA object for API middleware
 */
export const subscribeV4 = (planId: string, code: string, host, addOns = [], i18n: I18n) => {
  const params = { planId };
  // Prepare Body
  let dataBody = { host, addOns };
  if (code) {
    dataBody = _.assign(dataBody, {
      discountCode: code
    });
  }

  return apiService.sendRequest('admin-v3.subscription-v4.subscribe', { params, body: dataBody }, [
    SUBSCRIBE_PLAN_REQUEST,
    {
      type: SUBSCRIBE_PLAN_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: SUBSCRIBE_PLAN_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.SubscriptionV3.Actions.subscribe'),
          i18n
        )
    }
  ]);
};

/**
 * call to activate charge
 * @return {object} The RSAA object for API middleware
 */
export const activate = (chargeId: string, i18n: I18n) => {
  const params = { chargeId };

  return apiService.sendRequest('admin-v3.subscription-v3.activate', { params }, [
    ACTIVATE_CHARGE_REQUEST,
    {
      type: ACTIVATE_CHARGE_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: ACTIVATE_CHARGE_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.SubscriptionV3.Actions.activate'),
          i18n
        )
    }
  ]);
};

/**
 * call to activate charge
 * @return {object} The RSAA object for API middleware
 */
export const activateV4 = (chargeId: string, i18n: I18n) => {
  const params = { chargeId };

  return apiService.sendRequest('admin-v3.subscription-v4.activate', { params }, [
    ACTIVATE_CHARGE_REQUEST,
    {
      type: ACTIVATE_CHARGE_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: ACTIVATE_CHARGE_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.SubscriptionV3.Actions.activate'),
          i18n
        )
    }
  ]);
};

/**
 * Reset setup feedback state
 * @return {object} The RSAA object for API middleware
 */
export const clearDiscountCode = () => {
  return {
    type: CLEAR_DISCOUNT_CODE,
    payload: {}
  };
};

// API TO INTEGRATE FOR ADD ON

/**
 * Subscribe an addon
 * @param {*} addonName
 * @param {*} code discount code
 * @param {*} i18n
 * @returns
 */
export const subscribeAddon = (addonName: string, code: string, i18n) => {
  const params = { addonName };
  // Prepare Body
  const dataBody = { discountCode: code };

  return apiService.sendRequest(
    'admin-v3.subscription-v3.subscribe-addon',
    { params, body: dataBody },
    [
      SUBSCRIBE_ADDON_REQUEST,
      {
        type: SUBSCRIBE_ADDON_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data, addonName };
        }
      },
      {
        type: SUBSCRIBE_ADDON_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.SubscriptionV3.Actions.subscribeAddon'),
            i18n
          )
      }
    ]
  );
};

/**
 * call to unsubscribe addon
 * @return {object} The RSAA object for API middleware
 */
export const unsubscribeAddon = (addonId: string, name: string, i18n: I18n) => {
  const params = { addonId };

  return apiService.sendRequest(
    'admin-v3.subscription-v3.unsubscribe-addon',
    {
      params,
      body: {
        addOnName: name
      }
    },
    [
      UNSUBSCRIBE_ADDON_REQUEST,
      {
        type: UNSUBSCRIBE_ADDON_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data, addonName: name };
        }
      },
      {
        type: UNSUBSCRIBE_ADDON_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.SubscriptionV3.Actions.unsubscribe'),
            i18n
          )
      }
    ]
  );
};

/**
 * Get all list addon of store subscribed | trial | unsubscribe
 * @param {*} i18n
 * @returns
 */
export const getAddons = (i18n: I18n) => {
  return apiService.sendRequest('admin-v3.subscription-v3.get-addons', {}, [
    GET_ADDONS_REQUEST,
    {
      type: GET_ADDONS_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_ADDONS_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.SubscriptionV3.Actions.getAddons'),
          i18n
        )
    }
  ]);
};

/**
 * Get addon with current status
 * @param {string} addonName
 * @param {*} i18n
 * @returns
 */
export const getAddonStatus = (addonName, i18n: I18n) => {
  const params = { addonName };

  return apiService.sendRequest('admin-v3.subscription-v3.get-addon', { params }, [
    GET_ADDON_STATUS_REQUEST,
    {
      type: GET_ADDON_STATUS_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data, addonName };
      }
    },
    {
      type: GET_ADDON_STATUS_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.SubscriptionV3.Actions.getAddons'),
          i18n
        )
    }
  ]);
};
/**
 * Get list package plans store can subscribe
 * @param {*} i18n
 * @returns
 */
export const getPackagePlans = (i18n: I18n) => {
  return apiService.sendRequest('admin-v3.subscription-v3.get-package-plans', {}, [
    GET_PACKAGE_PLANS_REQUEST,
    {
      type: GET_PACKAGE_PLANS_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_PACKAGE_PLANS_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.SubscriptionV3.Actions.getAddons'),
          i18n
        )
    }
  ]);
};

/**
 * Get detail invoice for plan of store
 * @param {*} i18n
 * @returns
 */
export const getDetailInvoice = i18n => {
  return apiService.sendRequest('admin-v3.subscription-v3.get-invoice', {}, [
    GET_INVOICE_PLANS_REQUEST,
    {
      type: GET_INVOICE_PLANS_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);
        return { data };
      }
    },
    {
      type: GET_INVOICE_PLANS_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.SubscriptionV3.Actions.getInvoiceFailure'),
          i18n
        )
    }
  ]);
};
